import React, { useEffect, useState } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  PDFDownloadLink,
} from "@react-pdf/renderer";

import QRCode from "qrcode";
import SchoolLogo from "./savationChristLogo.png";
import AvatarStudent from "./studentavv.png";

const formatMoney = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "0.00";
  }

  return Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getPaymentCurrency = (record) => {
  return record?.payment_currency === "USD"
    ? "USD"
    : "LRD";
};

const getCurrencyLabel = (currency) => {
  return currency === "USD"
    ? "USD"
    : "LD";
};

const getExchangeRate = (record) => {
  return Number(
    record?.exchange_rate || 0
  );
};

const getOriginalPaymentAmount = (record) => {
  if (
    record?.amount_paid !== undefined &&
    record?.amount_paid !== null
  ) {
    return Number(
      record.amount_paid || 0
    );
  }

  return Number(
    record?.amount || 0
  );
};

const getNormalizedLrdAmount = (record) => {
  const amountPaidLrd = Number(
    record?.amount_paid_lrd || 0
  );

  if (amountPaidLrd > 0) {
    return amountPaidLrd;
  }

  const currency =
    getPaymentCurrency(record);

  const originalAmount =
    getOriginalPaymentAmount(record);

  const exchangeRate =
    getExchangeRate(record);

  if (
    currency === "USD" &&
    exchangeRate > 0
  ) {
    return (
      originalAmount *
      exchangeRate
    );
  }

  return originalAmount;
};

const formatCurrency = (
  amount,
  currency = "LRD"
) => {
  return `${getCurrencyLabel(currency)} ${formatMoney(amount)}`;
};

const formatLrd = (amount) => {
  return `LD ${formatMoney(amount)}`;
};

const formatAllocation = (
  allocation,
  paymentCurrency,
  exchangeRate
) => {
  if (!allocation) {
    return "";
  }

  const feeType =
    allocation.fee_type ||
    allocation.name ||
    "Fee";

  if (
    allocation.amount_display
  ) {
    return `${feeType}: ${allocation.amount_display}`;
  }

  const amountLrd = Number(
    allocation.amount_lrd ??
      allocation.amount ??
      0
  );

  if (
    paymentCurrency === "USD" &&
    exchangeRate > 0
  ) {
    const usdAmount =
      amountLrd /
      exchangeRate;

    return `${feeType}: USD ${formatMoney(
      usdAmount
    )}`;
  }

  return `${feeType}: LD ${formatMoney(
    amountLrd
  )}`;
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 28,
    paddingLeft: 32,
    paddingRight: 32,
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
    color: "#1f1f1f",
  },

  receiptWrapper: {
    border: "1 solid #bdbdbd",
    borderRadius: 6,
    padding: 10,
    marginBottom: 16,
  },

  header: {
    flexDirection: "row",
    alignItems: "center",
    borderBottom: "1 solid #d0d0d0",
    paddingBottom: 8,
    marginBottom: 8,
  },

  logo: {
    width: 52,
    height: 52,
    objectFit: "contain",
    marginRight: 10,
  },

  schoolInfo: {
    flex: 1,
  },

  schoolName: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#4b0714",
    marginBottom: 2,
  },

  schoolAddress: {
    fontSize: 8,
    color: "#555555",
    marginBottom: 2,
  },

  schoolContact: {
    fontSize: 8,
    color: "#555555",
  },

  paymentTitle: {
    marginTop: 4,
    fontSize: 9,
    fontWeight: "bold",
    color: "#4b0714",
  },

  copyTitle: {
    textAlign: "center",
    fontSize: 11,
    fontWeight: "bold",
    color: "#4b0714",
    marginBottom: 8,
    letterSpacing: 0.5,
  },

  studentSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: "1 solid #dedede",
    paddingBottom: 8,
    marginBottom: 8,
  },

  studentDetails: {
    flexDirection: "row",
    flex: 1,
  },

  avatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    objectFit: "cover",
    marginRight: 8,
  },

  studentText: {
    flex: 1,
    fontSize: 8.5,
    color: "#333333",
    lineHeight: 1.5,
  },

  studentName: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#1f1f1f",
    marginBottom: 2,
  },

  label: {
    fontWeight: "bold",
    color: "#4b0714",
  },

  qrCode: {
    width: 62,
    height: 62,
    marginLeft: 8,
  },

  detailsSection: {
    borderBottom: "1 solid #dedede",
    paddingBottom: 7,
    marginBottom: 8,
  },

  detailRow: {
    flexDirection: "row",
    marginBottom: 3,
  },

  detailLabel: {
    width: 80,
    fontSize: 8.5,
    fontWeight: "bold",
    color: "#4b0714",
  },

  detailValue: {
    flex: 1,
    fontSize: 8.5,
    color: "#333333",
  },

  servicesText: {
    fontSize: 8.5,
    color: "#333333",
    lineHeight: 1.4,
  },

  amountSection: {
    flexDirection: "row",
    gap: 6,
    marginTop: 5,
    marginBottom: 8,
  },

  amountBox: {
    flex: 1,
    border: "1 solid #bdbdbd",
    borderRadius: 4,
    paddingVertical: 5,
    paddingHorizontal: 5,
    backgroundColor: "#f3f3f3",
  },

  amountLabel: {
    fontSize: 7.5,
    color: "#555555",
    textAlign: "center",
    marginBottom: 2,
  },

  amountValue: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#1f1f1f",
    textAlign: "center",
  },

  amountSubValue: {
    fontSize: 7,
    color: "#555555",
    textAlign: "center",
    marginTop: 2,
  },

  footerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 4,
  },

  dateText: {
    fontSize: 8,
    color: "#555555",
  },

  signature: {
    width: 130,
    textAlign: "center",
  },

  signatureLine: {
    borderBottom: "1 solid #555555",
    marginBottom: 3,
  },

  signatureText: {
    fontSize: 7.5,
    color: "#555555",
  },

  receiptNumber: {
    fontSize: 7.5,
    color: "#777777",
    marginTop: 5,
  },

  separator: {
    borderBottom: "1 solid #4b0714",
    marginTop: 2,
    marginBottom: 10,
  },

  barcodeText: {
    fontSize: 7,
    textAlign: "center",
    color: "#777777",
    marginTop: 5,
  },
});

const ReceiptCopy = ({
  copyType,
  receiptData,
  qrCodeUrl,
}) => {
  return (
    <View
      style={styles.receiptWrapper}
      wrap={false}
    >
      <View style={styles.header}>
        <Image
          src={SchoolLogo}
          style={styles.logo}
        />

        <View style={styles.schoolInfo}>
          <Text style={styles.schoolName}>
            Salvation In Christ School System
          </Text>

          <Text style={styles.schoolAddress}>
            Zazay Community, Paynesville City, Monrovia
          </Text>

          <Text style={styles.schoolContact}>
            Contact: +231-776-494-010 / +231-880-585-855 / +231-770-540-258
          </Text>

          <Text style={styles.paymentTitle}>
            {receiptData.last_installment} Payment
          </Text>
        </View>
      </View>

      <Text style={styles.copyTitle}>
        {copyType}
      </Text>

      <View style={styles.studentSection}>
        <View style={styles.studentDetails}>
          <Image
            src={receiptData.profile_image}
            style={styles.avatar}
          />

          <View style={styles.studentText}>
            <Text style={styles.studentName}>
              {receiptData.student_name}
            </Text>

            <Text>
              <Text style={styles.label}>
                Class:{" "}
              </Text>
              {receiptData.grade_class_name}
            </Text>

            <Text>
              <Text style={styles.label}>
                Type:{" "}
              </Text>
              {receiptData.student_type} student
            </Text>

            <Text>
              <Text style={styles.label}>
                Yearly Fee:{" "}
              </Text>
              {receiptData.yearly_fee}
            </Text>

            <Text>
              <Text style={styles.label}>
                Overall Balance:{" "}
              </Text>
              {receiptData.balance_fee}
            </Text>
          </View>
        </View>

        {qrCodeUrl && (
          <Image
            src={qrCodeUrl}
            style={styles.qrCode}
          />
        )}
      </View>

      <View style={styles.detailsSection}>
        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>
            Receipt ID:
          </Text>

          <Text style={styles.detailValue}>
            {receiptData.receipt_number}
          </Text>
        </View>

        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>
            Payer:
          </Text>

          <Text style={styles.detailValue}>
            {receiptData.payer_name}
          </Text>
        </View>

        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>
            Currency:
          </Text>

          <Text style={styles.detailValue}>
            {receiptData.payment_currency === "USD"
              ? "USD"
              : "LD"}
          </Text>
        </View>

        {receiptData.payment_currency ===
          "USD" && (
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>
              Exchange Rate:
            </Text>

            <Text style={styles.detailValue}>
              1 USD = LD{" "}
              {formatMoney(
                receiptData.exchange_rate
              )}
            </Text>
          </View>
        )}

        <View>
          <Text style={styles.detailLabel}>
            Services:
          </Text>

          <Text style={styles.servicesText}>
            {receiptData.services}
          </Text>
        </View>
      </View>

      <View style={styles.amountSection}>
        <View style={styles.amountBox}>
          <Text style={styles.amountLabel}>
            AMOUNT DUE
          </Text>

          <Text style={styles.amountValue}>
            {receiptData.amount_due}
          </Text>
        </View>

        <View style={styles.amountBox}>
          <Text style={styles.amountLabel}>
            AMOUNT PAID
          </Text>

          <Text style={styles.amountValue}>
            {receiptData.amount_paid}
          </Text>

          {receiptData.payment_currency ===
            "USD" && (
            <Text style={styles.amountSubValue}>
              ≈{" "}
              {receiptData.amount_paid_lrd}
            </Text>
          )}
        </View>

        <View style={styles.amountBox}>
          <Text style={styles.amountLabel}>
            BALANCE
          </Text>

          <Text style={styles.amountValue}>
            {receiptData.balance}
          </Text>
        </View>
      </View>

      <View style={styles.footerSection}>
        <Text style={styles.dateText}>
          Date: {receiptData.date}
        </Text>

        <View style={styles.signature}>
          <View style={styles.signatureLine} />

          <Text style={styles.signatureText}>
            Finance Office SICSS
          </Text>
        </View>
      </View>

      <Text style={styles.receiptNumber}>
        Receipt Reference:{" "}
        {receiptData.receipt_number}
      </Text>

      <Text style={styles.barcodeText}>
        Official School Payment Receipt - Polu Integrated School Management System
      </Text>
    </View>
  );
};

const ReceiptPDF = ({
  receiptData,
  qrCodeUrl,
}) => {
  return (
    <Document>
      <Page
        size="A4"
        style={styles.page}
      >
        <ReceiptCopy
          copyType="STUDENT COPY"
          receiptData={receiptData}
          qrCodeUrl={qrCodeUrl}
        />

        <ReceiptCopy
          copyType="FINANCE COPY"
          receiptData={receiptData}
          qrCodeUrl={qrCodeUrl}
        />
      </Page>
    </Document>
  );
};

const ReceiptDialog = ({
  open,
  onClose,
  studentDetails,
  currentFeeIndex,
}) => {
  const [receiptData, setReceiptData] =
    useState(null);

  const [qrCodeUrl, setQrCodeUrl] =
    useState("");

  useEffect(() => {
    if (
      !studentDetails ||
      currentFeeIndex == null
    ) {
      return;
    }

    const fee =
      studentDetails.fee_records?.[
        currentFeeIndex
      ];

    if (!fee) {
      return;
    }

    const paymentCurrency =
      getPaymentCurrency(fee);

    const exchangeRate =
      getExchangeRate(fee);

    const originalPaymentAmount =
      getOriginalPaymentAmount(fee);

    const normalizedLrdAmount =
      getNormalizedLrdAmount(fee);

    const allocations =
      fee.allocations || [];

    const allocationText =
      allocations
        .map((allocation) =>
          formatAllocation(
            allocation,
            paymentCurrency,
            exchangeRate
          )
        )
        .filter(Boolean)
        .join(", ");

    const receiptNumber =
      `REC-${Date.now()}`;

    const data = {
      last_installment:
        fee.last_installment,

      student_name:
        studentDetails.student_name,

      grade_class_name:
        studentDetails.grade_class_name,

      student_type:
        studentDetails.student_type,

      profile_image:
        AvatarStudent,

      yearly_fee:
        formatLrd(
          fee.yearly_school_fee
        ),

      balance_fee:
        formatLrd(
          fee.overall_balance
        ),

      receipt_number:
        receiptNumber,

      payer_name:
        fee.payer_name || "N/A",

      payment_currency:
        paymentCurrency,

      exchange_rate:
        exchangeRate,

      amount_paid_original:
        originalPaymentAmount,

      amount_paid_lrd_value:
        normalizedLrdAmount,

      services:
        allocationText ||
        "No fee allocation recorded",

      amount_due:
        formatLrd(
          fee.fee_charge
        ),

      amount_paid:
        formatCurrency(
          originalPaymentAmount,
          paymentCurrency
        ),

      amount_paid_lrd:
        formatLrd(
          normalizedLrdAmount
        ),

      balance:
        formatLrd(
          fee.balance
        ),

      date:
        new Date(
          fee.created_at ||
            fee.date_paid ||
            Date.now()
        ).toLocaleString(),
    };

    setReceiptData(data);
    setQrCodeUrl("");

    const qrText = [
      "STUDENT RECEIPT",
      "-------------------------",
      `Installment: ${data.last_installment || ""}`,
      `Receipt ID: ${data.receipt_number}`,
      `Student Name: ${data.student_name || ""}`,
      `Class: ${data.grade_class_name || ""}`,
      `Payment Currency: ${
        data.payment_currency === "USD"
          ? "USD"
          : "LD"
      }`,
      `Amount Paid: ${data.amount_paid}`,
      data.payment_currency === "USD"
        ? `Exchange Rate: 1 USD = LD ${formatMoney(
            data.exchange_rate
          )}`
        : "",
      data.payment_currency === "USD"
        ? `LRD Equivalent: ${data.amount_paid_lrd}`
        : "",
      `Amount Due: ${data.amount_due}`,
      `Fee Allocations: ${data.services}`,
      `Balance: ${data.balance}`,
      `Date: ${data.date}`,
    ]
      .filter(Boolean)
      .join("\n");

    QRCode.toDataURL(
      qrText,
      {
        width: 390,
        margin: 1,
      }
    )
      .then((url) => {
        setQrCodeUrl(url);
      })
      .catch((error) => {
        console.error(
          "QR Code Error:",
          error
        );
      });
  }, [
    studentDetails,
    currentFeeIndex,
  ]);

  if (
    !receiptData ||
    !open
  ) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor:
          "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 850,
          maxHeight: "95vh",
          overflowY: "auto",
          backgroundColor: "#ffffff",
          borderRadius: 8,
          padding: 24,
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 650,
            margin: "0 auto",
            fontFamily:
              "Arial, sans-serif",
            color: "#222222",
          }}
        >
          {[
            "STUDENT COPY",
            "FINANCE COPY",
          ].map((copyType) => (
            <div
              key={copyType}
              style={{
                border:
                  "1px solid #bdbdbd",
                borderRadius: 6,
                padding: 12,
                marginBottom: 18,
                backgroundColor:
                  "#ffffff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  borderBottom:
                    "1px solid #d0d0d0",
                  paddingBottom: 8,
                  marginBottom: 8,
                }}
              >
                <img
                  src={SchoolLogo}
                  alt="School Logo"
                  style={{
                    width: 52,
                    height: 52,
                    objectFit:
                      "contain",
                    marginRight: 10,
                  }}
                />

                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight:
                        "bold",
                      color:
                        "#4b0714",
                    }}
                  >
                    Salvation In Christ School System
                  </div>

                  <div
                    style={{
                      fontSize: 9,
                      color:
                        "#555555",
                    }}
                  >
                    Zazay Community, Paynesville City, Monrovia
                  </div>

                  <div
                    style={{
                      fontSize: 9,
                      color:
                        "#555555",
                    }}
                  >
                    Contact: +231-776-494-010 / +231-880-585-855 / +231-770-540-258
                  </div>

                  <div
                    style={{
                      fontSize: 10,
                      fontWeight:
                        "bold",
                      color:
                        "#4b0714",
                      marginTop: 3,
                    }}
                  >
                    {
                      receiptData.last_installment
                    }{" "}
                    Payment
                  </div>
                </div>
              </div>

              <div
                style={{
                  textAlign:
                    "center",
                  fontSize: 12,
                  fontWeight:
                    "bold",
                  color:
                    "#4b0714",
                  marginBottom: 8,
                }}
              >
                {copyType}
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  borderBottom:
                    "1px solid #dedede",
                  paddingBottom: 8,
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems:
                      "center",
                  }}
                >
                  <img
                    src={
                      receiptData.profile_image
                    }
                    alt="Student"
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius:
                        "50%",
                      objectFit:
                        "cover",
                      marginRight: 8,
                    }}
                  />

                  <div
                    style={{
                      fontSize: 9,
                      lineHeight:
                        1.5,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight:
                          "bold",
                        marginBottom:
                          2,
                      }}
                    >
                      {
                        receiptData.student_name
                      }
                    </div>

                    <div>
                      <strong
                        style={{
                          color:
                            "#4b0714",
                        }}
                      >
                        Class:
                      </strong>{" "}
                      {
                        receiptData.grade_class_name
                      }
                    </div>

                    <div>
                      <strong
                        style={{
                          color:
                            "#4b0714",
                        }}
                      >
                        Type:
                      </strong>{" "}
                      {
                        receiptData.student_type
                      }{" "}
                      student
                    </div>

                    <div>
                      <strong
                        style={{
                          color:
                            "#4b0714",
                        }}
                      >
                        Yearly Fee:
                      </strong>{" "}
                      {
                        receiptData.yearly_fee
                      }
                    </div>

                    <div>
                      <strong
                        style={{
                          color:
                            "#4b0714",
                        }}
                      >
                        Overall Balance:
                      </strong>{" "}
                      {
                        receiptData.balance_fee
                      }
                    </div>
                  </div>
                </div>

                {qrCodeUrl && (
                  <img
                    src={qrCodeUrl}
                    alt="QR Code"
                    style={{
                      width: 65,
                      height: 65,
                    }}
                  />
                )}
              </div>

              <div
                style={{
                  borderBottom:
                    "1px solid #dedede",
                  paddingBottom: 8,
                  marginBottom: 8,
                  fontSize: 9,
                }}
              >
                <div
                  style={{
                    marginBottom: 3,
                  }}
                >
                  <strong
                    style={{
                      color:
                        "#4b0714",
                    }}
                  >
                    Receipt ID:
                  </strong>{" "}
                  {
                    receiptData.receipt_number
                  }
                </div>

                <div
                  style={{
                    marginBottom: 3,
                  }}
                >
                  <strong
                    style={{
                      color:
                        "#4b0714",
                    }}
                  >
                    Payer:
                  </strong>{" "}
                  {
                    receiptData.payer_name
                  }
                </div>

                <div
                  style={{
                    marginBottom: 3,
                  }}
                >
                  <strong
                    style={{
                      color:
                        "#4b0714",
                    }}
                  >
                    Currency:
                  </strong>{" "}
                  {receiptData.payment_currency ===
                  "USD"
                    ? "USD"
                    : "LD"}
                </div>

                {receiptData.payment_currency ===
                  "USD" && (
                  <div
                    style={{
                      marginBottom: 3,
                    }}
                  >
                    <strong
                      style={{
                        color:
                          "#4b0714",
                      }}
                    >
                      Exchange Rate:
                    </strong>{" "}
                    1 USD = LD{" "}
                    {formatMoney(
                      receiptData.exchange_rate
                    )}
                  </div>
                )}

                <div
                  style={{
                    marginBottom: 3,
                  }}
                >
                  <strong
                    style={{
                      color:
                        "#4b0714",
                    }}
                  >
                    Services:
                  </strong>{" "}
                  {
                    receiptData.services
                  }
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 6,
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    border:
                      "1px solid #bdbdbd",
                    borderRadius: 4,
                    padding: 6,
                    textAlign:
                      "center",
                    backgroundColor:
                      "#f3f3f3",
                  }}
                >
                  <div
                    style={{
                      fontSize: 8,
                      color:
                        "#555555",
                    }}
                  >
                    AMOUNT DUE
                  </div>

                  <strong>
                    {
                      receiptData.amount_due
                    }
                  </strong>
                </div>

                <div
                  style={{
                    flex: 1,
                    border:
                      "1px solid #bdbdbd",
                    borderRadius: 4,
                    padding: 6,
                    textAlign:
                      "center",
                    backgroundColor:
                      "#f3f3f3",
                  }}
                >
                  <div
                    style={{
                      fontSize: 8,
                      color:
                        "#555555",
                    }}
                  >
                    AMOUNT PAID
                  </div>

                  <strong>
                    {
                      receiptData.amount_paid
                    }
                  </strong>

                  {receiptData.payment_currency ===
                    "USD" && (
                    <div
                      style={{
                        fontSize: 7,
                        color:
                          "#555555",
                        marginTop: 2,
                      }}
                    >
                      ≈{" "}
                      {
                        receiptData.amount_paid_lrd
                      }
                    </div>
                  )}
                </div>

                <div
                  style={{
                    flex: 1,
                    border:
                      "1px solid #bdbdbd",
                    borderRadius: 4,
                    padding: 6,
                    textAlign:
                      "center",
                    backgroundColor:
                      "#f3f3f3",
                  }}
                >
                  <div
                    style={{
                      fontSize: 8,
                      color:
                        "#555555",
                    }}
                  >
                    BALANCE
                  </div>

                  <strong>
                    {
                      receiptData.balance
                    }
                  </strong>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "flex-end",
                  fontSize: 8,
                }}
              >
                <span>
                  Date:{" "}
                  {receiptData.date}
                </span>

                <div
                  style={{
                    textAlign:
                      "center",
                    width: 130,
                  }}
                >
                  <div
                    style={{
                      borderBottom:
                        "1px solid #555555",
                      marginBottom: 3,
                    }}
                  />

                  <span
                    style={{
                      color:
                        "#555555",
                    }}
                  >
                    Finance Office SICSS
                  </span>
                </div>
              </div>

              <div
                style={{
                  fontSize: 7,
                  color:
                    "#777777",
                  marginTop: 5,
                }}
              >
                Receipt Reference:{" "}
                {
                  receiptData.receipt_number
                }
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent:
              "flex-end",
            gap: 10,
            marginTop: 20,
          }}
        >
          <PDFDownloadLink
            document={
              <ReceiptPDF
                receiptData={
                  receiptData
                }
                qrCodeUrl={
                  qrCodeUrl
                }
              />
            }
            fileName={`${receiptData.receipt_number}.pdf`}
            style={{
              textDecoration:
                "none",
              backgroundColor:
                "#4b0714",
              color:
                "#ffffff",
              padding:
                "10px 18px",
              borderRadius: 5,
              fontSize: 14,
              fontWeight:
                "bold",
            }}
          >
            {({ loading }) =>
              loading
                ? "Generating PDF..."
                : "Export PDF"
            }
          </PDFDownloadLink>

          <button
            onClick={onClose}
            style={{
              backgroundColor:
                "#eeeeee",
              border:
                "1px solid #bdbdbd",
              color:
                "#333333",
              padding:
                "10px 18px",
              borderRadius: 5,
              cursor:
                "pointer",
              fontSize: 14,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptDialog;

